import * as React from 'react';
import { t } from '@reos/i18n';
import type { IntakeContextDto } from '../../lib/field-intake-api.js';
import { fieldLabel, formatValue } from './values.js';

/**
 * Krok UXV4c body 1+2 — start naboru: adresa predvyplnena (zakazka nebo
 * udalost kalendare), nahore „REOS už zná" (fakta zakazky z provereni) a
 * POVINNY prepinac souhlasu majitele. Bez souhlasu jde jen „diktuju si sám".
 */

export function knownSummary(context: IntakeContextDto | null): string | null {
  if (context === null || context.known.length === 0) return null;
  const official = context.known.filter((f) => f.provenanceStatus === 'VERIFIED' || f.sourceType === 'registry');
  const pick = (official.length > 0 ? official : context.known).slice(0, 3);
  return pick.map((f) => `${fieldLabel(f.factKey)} ${formatValue(f.factKey, f.value)}`).join(', ');
}

export function StartStep({
  context,
  address,
  onAddressChange,
  consent,
  onConsentChange,
  onStart,
  busy,
}: {
  readonly context: IntakeContextDto | null;
  readonly address: string;
  readonly onAddressChange: (value: string) => void;
  readonly consent: boolean;
  readonly onConsentChange: (value: boolean) => void;
  readonly onStart: () => void;
  readonly busy: boolean;
}): React.JSX.Element {
  const summary = knownSummary(context);
  return (
    <div className="rh-fi-start" data-testid="fi-start">
      {/*
        Krok NAB1 — na desktopu stoji formular vlevo a vysvetleni vpravo
        (`nabor.css`), oboji ve vycentrovane sirce `--rh-fi-start-max`. Do NAB1
        to byla jedna mobilni kolona pres celych 1440 px s prazdnou polovinou
        obrazovky (nalez R.B. 7. 10. 2026 bod 1). Na mobilu je to tataz jedina
        kolona jako dosud, vysvetleni se neukazuje.
      */}
      <div className="rh-fi-start__cols">
        <div className="rh-fi-stack rh-fi-start__form">
          <section className="rh-v4-group rh-fi-card" data-testid="fi-property">
            <div className="rh-fi-card__title">{context?.case?.title ?? (address !== '' ? address : t('webAdmin.fieldIntake.start.noCase', { locale: 'cs' }))}</div>
            <div className="rh-fi-card__sub" data-testid="fi-known">
              {summary === null
                ? t('webAdmin.fieldIntake.start.knownNothing', { locale: 'cs' })
                : t('webAdmin.fieldIntake.start.known', { locale: 'cs', values: { facts: summary } })}
            </div>
          </section>

          <section className="rh-v4-group">
            <label className="rh-v4-form-row">
              <span className="rh-v4-form-row__label">{t('webAdmin.fieldIntake.start.address', { locale: 'cs' })}</span>
              <input
                className="rh-input rh-fi-input"
                value={address}
                onChange={(e) => onAddressChange(e.target.value)}
                data-testid="fi-address"
              />
            </label>
            <label className="rh-v4-form-row rh-fi-switch-row">
              <span className="rh-v4-form-row__label">{t('webAdmin.fieldIntake.start.consent', { locale: 'cs' })}</span>
              <input
                type="checkbox"
                role="switch"
                className="rh-fi-switch"
                checked={consent}
                onChange={(e) => onConsentChange(e.target.checked)}
                aria-checked={consent}
                data-testid="fi-consent"
              />
              <span className="rh-v4-form-row__hint">
                {consent
                  ? t('webAdmin.fieldIntake.start.consentOnHint', { locale: 'cs' })
                  : t('webAdmin.fieldIntake.start.consentOffHint', { locale: 'cs' })}
              </span>
            </label>
          </section>

          <button
            type="button"
            className="rh-v4-btn rh-v4-btn--filled rh-v4-btn--block"
            onClick={onStart}
            disabled={busy}
            data-testid="fi-start-btn"
          >
            {consent
              ? t('webAdmin.fieldIntake.start.recordConversation', { locale: 'cs' })
              : t('webAdmin.fieldIntake.start.recordSelf', { locale: 'cs' })}
          </button>
        </div>

        <aside className="rh-v4-group rh-fi-card rh-fi-start__why" data-testid="fi-start-why">
          <div className="rh-fi-card__title">{t('webAdmin.fieldIntake.start.deskTitle', { locale: 'cs' })}</div>
          <ol className="rh-fi-start__steps">
            <li>{t('webAdmin.fieldIntake.start.deskStep1', { locale: 'cs' })}</li>
            <li>{t('webAdmin.fieldIntake.start.deskStep2', { locale: 'cs' })}</li>
            <li>{t('webAdmin.fieldIntake.start.deskStep3', { locale: 'cs' })}</li>
          </ol>
          <div className="rh-fi-card__sub">{t('webAdmin.fieldIntake.start.deskPoints', { locale: 'cs' })}</div>
        </aside>
      </div>
    </div>
  );
}
